/**
 * Backup Service
 *
 * API calls for backup management
 */

import api from '../utils/api';
import config from '../config';

const backupService = {
  /**
   * Get backup initialization data
   * @returns {Promise} Response data with units and other init data
   */
  getInit: async () => {
    return api.get(config.endpoints.backup.init, { subordinateLevelId: 1 });
  },

  /**
   * Get backup config for a specific unit
   * @param {number} unitId - Unit ID
   * @returns {Promise} Response data with backup config
   */
  getBackupConfig: async (unitId) => {
    return api.get(`${config.endpoints.backup.getConfig}/${unitId}/backup-config`);
  },

  /**
   * Save backup config for a specific unit
   * @param {number} unitId - Unit ID
   * @param {Object} configData - Backup config data
   * @returns {Promise} Response data
   */
  saveBackupConfig: async (unitId, configData) => {
    return api.post(`${config.endpoints.backup.getConfig}/${unitId}/backup-config`, configData);
  },

  /**
   * Trigger manual backup for a specific unit
   * @param {number} unitId - Unit ID
   * @returns {Promise} Response data
   */
  manualBackup: async (unitId) => {
    return api.post(`${config.endpoints.backup.getConfig}/${unitId}/backup-manual`);
  },

  /**
   * Get backup logs list
   * @param {Object} params - Query parameters (page, size, filters)
   * @returns {Promise} Response data with backup logs list
   */
  getBackupLogs: async (params = {}) => {
    return api.get(config.endpoints.backupLogs.list, { subordinateLevelId: 1, ...params });
  },

  /**
   * Mark backup log as confirmed
   * @param {number} id - Backup log ID
   * @returns {Promise} Response data
   */
  markBackupLog: async (id) => {
    return api.post(`${config.endpoints.backupLogs.list}/${id}/marked`);
  },

  /**
   * Restore backup from a backup log
   * @param {number} id - Backup log ID
   * @returns {Promise} Response data
   */
  restoreBackupLog: async (id) => {
    return api.post(`${config.endpoints.backupLogs.list}/${id}/restore`);
  },

  /**
   * Download backup file
   * @param {number} id - Backup log ID
   * @returns {Promise} Download URL or triggers download
   */
  downloadBackupFile: async (id) => {
    return api.download(`${config.endpoints.backupLogs.list}/${id}/download`);
  },

  /**
   * Restore data from file
   * @param {number} unitId - Unit ID
   * @param {File} file - Backup file to restore
   * @returns {Promise} Response data
   */
  restoreFromFile: async (unitId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`${config.endpoints.backup.getConfig}/${unitId}/restore`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Get restore logs list
   * @param {Object} params - Query parameters (page, size, filters)
   * @returns {Promise} Response data with restore logs list
   */
  getRestoreLogs: async (params = {}) => {
    return api.get(config.endpoints.restoreLogs.list, { subordinateLevelId: 1, ...params });
  },
};

export default backupService;
